import type { ThunkAction, ThunkDispatch } from "@reduxjs/toolkit";
import type { Unsubscribe } from "firebase/firestore";

import type { Dish } from "@dashboard/types/dish";
import type { ReduxState } from "@dashboard/types/reduxState";
import type { ThunkExtraArgument } from "@dashboard/types/thunk";

import type { DishReducerAction } from "../reducers/dishReducer";
import type { ErrorReducerAction } from "../reducers/errorReducer";
import { addError } from "./addErrorAction";
import { fetchResolvedEdibles } from "./resolvedEdibleActions";

type ThunkActionReturn = ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkExtraArgument,
  DishReducerAction
>;

type DishSaveThunkAction = ThunkAction<
  Promise<boolean>,
  ReduxState,
  ThunkExtraArgument,
  DishReducerAction | ErrorReducerAction
>;

export type DishThunkDispatch = ThunkDispatch<
  ReduxState,
  ThunkExtraArgument,
  DishReducerAction | ErrorReducerAction
>;

export const setDish: (dish: Dish, isNew: boolean) => DishSaveThunkAction = (
  dish: Dish,
  isNew: boolean
) => {
  return async (
    dispatch: DishThunkDispatch,
    _getState: () => ReduxState,
    { dishApi }: ThunkExtraArgument
  ) => {
    try {
      await dishApi.setDish(dish, isNew);
      return true;
    } catch (error) {
      addError(dispatch)(error as Error | string);
      return false;
    }
  };
};

// Adds a single product to a dish as a field-level Firestore write
// (arrayUnion), so it cannot overwrite concurrent edits to the rest
// of the dish or its productIds.
export const addProductToDish: (
  dishId: string,
  productId: string
) => ThunkActionReturn = (dishId: string, productId: string) => {
  return (
    dispatch: DishThunkDispatch,
    getState: () => ReduxState,
    { dishApi }: ThunkExtraArgument
  ) => {
    const restaurantUuid = getState().restaurant.selectedRestaurant!.uuid;
    return dishApi
      .addProductToDish(restaurantUuid, dishId, productId)
      .catch(addError(dispatch));
  };
};

// Counterpart of addProductToDish, backed by arrayRemove.
export const removeProductFromDish: (
  dishId: string,
  productId: string
) => ThunkActionReturn = (dishId: string, productId: string) => {
  return (
    dispatch: DishThunkDispatch,
    getState: () => ReduxState,
    { dishApi }: ThunkExtraArgument
  ) => {
    const restaurantUuid = getState().restaurant.selectedRestaurant!.uuid;
    return dishApi
      .removeProductFromDish(restaurantUuid, dishId, productId)
      .catch(addError(dispatch));
  };
};

export const deleteDish: (
  restaurantUuid: string,
  dishId: string
) => ThunkActionReturn = (restaurantUuid: string, dishId: string) => {
  // Reuse the bulkDeleteDishes action to delete the dish
  return bulkDeleteDishes(restaurantUuid, [dishId]);
};

// Deletes multiple dishes at once
export const bulkDeleteDishes: (
  restaurantUuid: string,
  dishIds: string[]
) => ThunkActionReturn = (restaurantUuid: string, dishIds: string[]) => {
  return (
    dispatch: DishThunkDispatch,
    getState: () => ReduxState,
    { dishApi }: ThunkExtraArgument
  ) => {
    // Get current state to check which dishes are in menu sections
    const state = getState();
    const menuSections = Object.values(state.menu.menuSectionsByMenuId).flat();

    // Create a Set of dish IDs that are in menu sections
    const dishIdsInMenus = new Set<string>();
    menuSections.forEach((section) => {
      section.dishIdsInDisplayOrder.forEach((dishId) => {
        dishIdsInMenus.add(dishId);
      });
    });

    // Separate dishes into two groups
    const dishIdsInMenuSections = dishIds.filter((id) =>
      dishIdsInMenus.has(id)
    );
    const dishIdsNotInMenus = dishIds.filter((id) => !dishIdsInMenus.has(id));

    // Process dishes
    const processDishes = async () => {
      // Process dishes in menu sections sequentially to avoid transaction conflicts
      for (const dishId of dishIdsInMenuSections) {
        await dishApi.deleteDish(restaurantUuid, dishId);
      }

      // Process dishes not in menus in parallel batches for better performance
      const batchSize = 100;
      const totalBatches = Math.ceil(dishIdsNotInMenus.length / batchSize);

      for (let i = 0; i < totalBatches; i++) {
        const start = i * batchSize;
        const end = Math.min(start + batchSize, dishIdsNotInMenus.length);
        const batch = dishIdsNotInMenus.slice(start, end);

        // These can be processed in parallel since they don't affect menu sections
        await Promise.all(
          batch.map((dishId) => dishApi.deleteDish(restaurantUuid, dishId))
        );
      }
    };

    return processDishes().catch(addError(dispatch));
  };
};

// Live listener: returns the unsubscribe synchronously (not a promise).
export const subscribeToDishes =
  (
    restaurantUuid: string
  ): ThunkAction<
    Unsubscribe,
    ReduxState,
    ThunkExtraArgument,
    DishReducerAction
  > =>
  (dispatch, getState, { dishApi }) =>
    dishApi.subscribeToDishes(
      restaurantUuid,
      (payload) => {
        if (getState().dish.initialDishesLoading) {
          dispatch({ type: "FETCHED_DISHES", payload });
          return;
        }
        // Refresh resolved edibles after each change, since that data is now
        // stale. The resolvedEdibles state must be updated before the changed
        // dishes are put into state, so that every UI component finds a dish
        // it renders in the resolvedEdibles state.
        dispatch(fetchResolvedEdibles()).then(() =>
          dispatch({ type: "FETCHED_DISHES", payload })
        );
      },
      addError(dispatch)
    );

export const fetchDishesForRestaurant = (
  restaurantUuid: string
): ThunkAction<
  Promise<Dish[]>,
  ReduxState,
  ThunkExtraArgument,
  DishReducerAction
> => {
  return (_dispatch, _getState, { dishApi }) => {
    return dishApi.fetchDishes(restaurantUuid);
  };
};
